import { isSelfScroll } from "./selfScroll.js";

// Product cards drop their hover state after a few seconds of being hovered:
// the second image, the underlined name and the add-to-cart plus all retire
// and the card sits as it does at rest, even though the pointer never left.
// A card parked under a resting cursor otherwise holds its hover treatment
// indefinitely, which reads as the grid being stuck rather than as a hover.
//
// Timers hang off a WeakMap keyed by the card element rather than React state:
// a grid can hold dozens of cards, and re-rendering one on a timer it started
// itself would throw away the browser's own hover tracking mid-gesture. The
// weak key means a card that unmounts takes its entry with it.
const timers = new WeakMap();

export const HOVER_REST_MS = 3000;
export const HOVER_REST_CLASS = "product-card--hover-rest";

// While the page scrolls, cards slide under a pointer that is not moving, and
// every card it crosses would run its hover: the second photo, the underlined
// name, the plus. The photos crossfade over 0.25s, so a scroll left half-done
// swaps all over the grid, read as doubled pictures. The root carries
// SCROLLING_CLASS from the first scroll event until SCROLL_SETTLE_MS after the
// last one; the card hover rules in app.css apply only without it, and a card
// that was mid-hover snaps back to its main photo without a fade when it
// arrives. A card left under a resting pointer takes its hover once the page
// is still. Listens in the capture phase, so a scroll inside any container
// (the home page's sliders) counts too, except a row that scrolls itself
// (utils/selfScroll): the home page's NEW ARRIVALS drifts without end, but
// holds still while a mouse is over it, so its cards never slide under a
// resting pointer.
export const SCROLLING_CLASS = "cards-scrolling";
const SCROLL_SETTLE_MS = 300;
if (typeof window !== "undefined" && !window.__cardsScrollWatch) {
  window.__cardsScrollWatch = true;
  let settle = null;
  window.addEventListener("scroll", (e) => {
    if (isSelfScroll(e)) return;
    if (!settle) document.documentElement.classList.add(SCROLLING_CLASS);
    clearTimeout(settle);
    settle = setTimeout(() => {
      document.documentElement.classList.remove(SCROLLING_CLASS);
      settle = null;
    }, SCROLL_SETTLE_MS);
  }, { passive: true, capture: true });
}

/** Begin (or restart) the countdown to a card's rest state. */
export function startHoverRest(el, ms = HOVER_REST_MS) {
  if (!el) return;
  cancelHoverRest(el);
  timers.set(el, setTimeout(() => {
    el.classList.add(HOVER_REST_CLASS);
    timers.delete(el);
  }, ms));
}

/** Stop the countdown and put the card back in play, e.g. on pointer leave. */
export function cancelHoverRest(el) {
  if (!el) return;
  const t = timers.get(el);
  if (t) {
    clearTimeout(t);
    timers.delete(el);
  }
  el.classList.remove(HOVER_REST_CLASS);
}
